<template>
  <div class="timestamp-manager-modal modal-overlay" @click="$emit('close')">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2>
          <i class="fas fa-clock"></i>
          Manage Timestamps: {{ video.title }}
        </h2>
        <button @click="$emit('close')" class="modal-close">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <p class="info-text">
          Add timestamp markers to help viewers jump to specific topics in the video.
        </p>

        <!-- Add Timestamp Form -->
        <div class="add-timestamp-section">
          <h3>Add New Timestamp</h3>
          <div class="timestamp-form">
            <div class="form-row">
              <div class="form-group">
                <label>Time (MM:SS) *</label>
                <input
                  v-model="newTimestamp.time"
                  type="text"
                  placeholder="05:30"
                  class="form-input"
                />
              </div>
              <div class="form-group">
                <label>Title *</label>
                <input
                  v-model="newTimestamp.title"
                  type="text"
                  placeholder="Topic 1: Introduction"
                  class="form-input"
                />
              </div>
            </div>
            <div class="form-group">
              <label>Description</label>
              <input
                v-model="newTimestamp.description"
                type="text"
                placeholder="Brief description of this section..."
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>Thumbnail Options</label>
              
              <!-- Tab selection for thumbnail method -->
              <div class="thumbnail-method-tabs">
                <button
                  @click="thumbnailMethod = 'url'"
                  :class="['method-tab', { active: thumbnailMethod === 'url' }]"
                  type="button"
                >
                  <i class="fas fa-link"></i>
                  URL
                </button>
                <button
                  @click="thumbnailMethod = 'upload'"
                  :class="['method-tab', { active: thumbnailMethod === 'upload' }]"
                  type="button"
                >
                  <i class="fas fa-upload"></i>
                  Upload Slide
                </button>
                <button
                  v-if="isVimeoVideo"
                  @click="thumbnailMethod = 'vimeo'"
                  :class="['method-tab', { active: thumbnailMethod === 'vimeo' }]"
                  type="button"
                >
                  <i class="fab fa-vimeo"></i>
                  Vimeo
                </button>
              </div>

              <!-- URL Input -->
              <div v-if="thumbnailMethod === 'url'" class="thumbnail-input-section">
                <input
                  v-model="newTimestamp.thumbnail_url"
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  class="form-input"
                />
                <small>Paste a URL to an existing image</small>
              </div>

              <!-- File Upload -->
              <div v-if="thumbnailMethod === 'upload'" class="thumbnail-input-section">
                <div class="file-upload-wrapper">
                  <input
                    ref="thumbnailFileInput"
                    type="file"
                    accept="image/*"
                    @change="handleThumbnailUpload"
                    class="file-input"
                    id="thumbnail-file-input"
                  />
                  <label for="thumbnail-file-input" class="file-upload-label">
                    <i class="fas fa-cloud-upload-alt"></i>
                    {{ thumbnailFile ? thumbnailFile.name : 'Choose slide image...' }}
                  </label>
                </div>
                <small>Upload a slide screenshot or custom thumbnail image (JPG, PNG, GIF)</small>
              </div>

              <!-- Vimeo Auto-fetch -->
              <div v-if="thumbnailMethod === 'vimeo'" class="thumbnail-input-section">
                <button
                  @click="fetchThumbnail"
                  :disabled="!newTimestamp.time || fetchingThumbnail"
                  class="btn btn-outline btn-full"
                  type="button"
                >
                  <i class="fas fa-image" v-if="!fetchingThumbnail"></i>
                  <i class="fas fa-spinner fa-spin" v-else></i>
                  {{ fetchingThumbnail ? 'Fetching from Vimeo...' : 'Fetch Vimeo Thumbnail' }}
                </button>
                <small>Auto-fetch thumbnail from Vimeo video (note: may be the same for all timestamps)</small>
              </div>
            </div>
            
            <!-- Thumbnail Preview -->
            <div v-if="thumbnailPreviewUrl || newTimestamp.thumbnail_url" class="thumbnail-preview">
              <img :src="thumbnailPreviewUrl || newTimestamp.thumbnail_url.split('#')[0]" alt="Thumbnail preview" />
              <div class="thumbnail-time-badge">{{ newTimestamp.time }}</div>
            </div>
            <button
              @click="addTimestamp"
              :disabled="!newTimestamp.time || !newTimestamp.title"
              class="btn btn-primary"
            >
              <i class="fas fa-plus"></i>
              Add Timestamp
            </button>
          </div>
        </div>

        <!-- Timestamps List -->
        <div class="timestamps-list-section">
          <h3>Existing Timestamps ({{ timestamps.length }})</h3>
          
          <div v-if="timestamps.length === 0" class="empty-state-small">
            <i class="fas fa-clock"></i>
            <p>No timestamps yet. Add your first topic marker above.</p>
          </div>

          <div v-else class="timestamps-list">
            <div
              v-for="(timestamp, index) in sortedTimestamps"
              :key="timestamp.id || index"
              class="timestamp-item"
              :class="{ editing: editingTimestamp === timestamp.id }"
            >
              <div class="timestamp-number">{{ index + 1 }}</div>
              <div v-if="timestamp.slide_thumbnail || timestamp.thumbnail_url" class="timestamp-thumbnail">
                <img 
                  :src="timestamp.slide_thumbnail || timestamp.thumbnail_url.split('#')[0]" 
                  :alt="timestamp.title"
                />
                <div class="thumbnail-time-overlay">{{ formatTime(timestamp.timestamp_seconds) }}</div>
              </div>
              
              <!-- View Mode -->
              <div v-if="editingTimestamp !== timestamp.id" class="timestamp-info">
                <div class="timestamp-header">
                  <span class="timestamp-time">{{ formatTime(timestamp.timestamp_seconds) }}</span>
                  <h4>{{ timestamp.title }}</h4>
                </div>
                <p v-if="timestamp.description" class="timestamp-desc">
                  {{ timestamp.description }}
                </p>
              </div>
              
              <!-- Edit Mode -->
              <div v-else class="timestamp-edit-form">
                <div class="edit-row">
                  <input
                    v-model="editForm.time"
                    type="text"
                    placeholder="MM:SS"
                    class="edit-input time-input"
                  />
                  <input
                    v-model="editForm.title"
                    type="text"
                    placeholder="Title"
                    class="edit-input title-input"
                  />
                </div>
                <input
                  v-model="editForm.description"
                  type="text"
                  placeholder="Description (optional)"
                  class="edit-input desc-input"
                />
              </div>
              
              <!-- Action Buttons -->
              <div class="timestamp-actions">
                <template v-if="editingTimestamp !== timestamp.id">
                  <button
                    @click="startEdit(timestamp)"
                    class="btn btn-small btn-secondary"
                    title="Edit"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                  <button
                    @click="deleteTimestamp(timestamp)"
                    class="btn btn-small btn-danger"
                    title="Delete"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </template>
                <template v-else>
                  <button
                    @click="saveEdit(timestamp)"
                    class="btn btn-small btn-success"
                    title="Save"
                  >
                    <i class="fas fa-check"></i>
                  </button>
                  <button
                    @click="cancelEdit"
                    class="btn btn-small btn-secondary"
                    title="Cancel"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="$emit('close')" class="btn btn-secondary">
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'

export default {
  name: 'TimestampManager',
  props: {
    video: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'updated'],
  setup(props, { emit }) {
    const toast = useToast()
    const timestamps = ref([])
    const fetchingThumbnail = ref(false)
    const thumbnailMethod = ref('upload')
    const thumbnailFile = ref(null)
    const thumbnailPreviewUrl = ref('')
    const thumbnailFileInput = ref(null)
    const editingTimestamp = ref(null)
    const editForm = ref({
      time: '',
      title: '',
      description: ''
    })
    const newTimestamp = ref({
      time: '',
      title: '',
      description: '',
      thumbnail_url: ''
    })

    const isVimeoVideo = computed(() => {
      return props.video.video_url && props.video.video_url.includes('vimeo.com')
    })

    const sortedTimestamps = computed(() => {
      return [...timestamps.value].sort((a, b) => a.timestamp_seconds - b.timestamp_seconds)
    })

    const loadTimestamps = async () => {
      try {
        const api = (await import('@/services/api.js')).default
        const response = await api.get(`/lms/videos/${props.video.id}/`)
        if (response.data.timestamps) {
          timestamps.value = response.data.timestamps
        }
      } catch (err) {
        console.error('Error loading timestamps:', err)
      }
    }

    const parseTimeToSeconds = (timeString) => {
      // Parse MM:SS or HH:MM:SS format
      const parts = timeString.split(':').map(p => parseInt(p, 10))
      if (parts.length === 2) {
        // MM:SS
        return parts[0] * 60 + parts[1]
      } else if (parts.length === 3) {
        // HH:MM:SS
        return parts[0] * 3600 + parts[1] * 60 + parts[2]
      }
      return 0
    }

    const formatTime = (seconds) => {
      if (!seconds && seconds !== 0) return '0:00'
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    const handleThumbnailUpload = (event) => {
      const file = event.target.files[0]
      if (!file) return

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be smaller than 5MB')
        return
      }

      thumbnailFile.value = file

      // Create preview URL
      const reader = new FileReader()
      reader.onload = (e) => {
        thumbnailPreviewUrl.value = e.target.result
      }
      reader.readAsDataURL(file)

      toast.success('Image loaded - will be uploaded when you add the timestamp')
    }

    const fetchThumbnail = async () => {
      if (!newTimestamp.value.time) {
        toast.warning('Please enter a timestamp first')
        return
      }

      fetchingThumbnail.value = true
      try {
        const api = (await import('@/services/api.js')).default
        
        // Call backend with just the timestamp to trigger auto-fetch
        const timestampData = {
          video: props.video.id,
          timestamp_seconds: parseTimeToSeconds(newTimestamp.value.time),
          title: 'Preview', // Temporary title
          description: ''
        }

        const response = await api.post(`/lms/videos/${props.video.id}/add_timestamp/`, timestampData)
        
        if (response.data.success && response.data.timestamp.thumbnail_url) {
          newTimestamp.value.thumbnail_url = response.data.timestamp.thumbnail_url
          
          // Delete the preview timestamp we just created
          await api.delete(`/lms/timestamps/${response.data.timestamp.id}/`)
          
          toast.success('Thumbnail fetched successfully!')
        } else {
          toast.warning('Could not fetch thumbnail - ensure Vimeo access token is configured')
        }
      } catch (err) {
        console.error('Error fetching thumbnail:', err)
        toast.error('Failed to fetch thumbnail')
      } finally {
        fetchingThumbnail.value = false
      }
    }

    const addTimestamp = async () => {
      try {
        const api = (await import('@/services/api.js')).default
        
        // If we have a file to upload, use FormData
        if (thumbnailFile.value) {
          const formData = new FormData()
          formData.append('video', props.video.id)
          formData.append('timestamp_seconds', parseTimeToSeconds(newTimestamp.value.time))
          formData.append('title', newTimestamp.value.title)
          formData.append('description', newTimestamp.value.description || '')
          formData.append('slide_thumbnail', thumbnailFile.value)

          await api.post(`/lms/videos/${props.video.id}/add_timestamp/`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
          
          toast.success('Timestamp added with custom thumbnail!')
        } else {
          // No file upload, just use URL if provided
          const timestampData = {
            video: props.video.id,
            timestamp_seconds: parseTimeToSeconds(newTimestamp.value.time),
            title: newTimestamp.value.title,
            description: newTimestamp.value.description,
            thumbnail_url: newTimestamp.value.thumbnail_url || ''
          }

          const response = await api.post(`/lms/videos/${props.video.id}/add_timestamp/`, timestampData)
          
          toast.success('Timestamp added successfully')
          
          // If thumbnail was auto-fetched, show message
          if (response.data.timestamp && response.data.timestamp.thumbnail_url && !newTimestamp.value.thumbnail_url) {
            toast.info('Thumbnail auto-generated from Vimeo')
          }
        }
        
        // Reset form
        newTimestamp.value = { time: '', title: '', description: '', thumbnail_url: '' }
        thumbnailFile.value = null
        thumbnailPreviewUrl.value = ''
        thumbnailMethod.value = 'upload'
        if (thumbnailFileInput.value) {
          thumbnailFileInput.value.value = ''
        }
        
        await loadTimestamps()
        emit('updated')
      } catch (err) {
        console.error('Error adding timestamp:', err)
        toast.error('Failed to add timestamp: ' + (err.response?.data?.error || err.message))
      }
    }

    const deleteTimestamp = async (timestamp) => {
      if (!confirm(`Delete timestamp "${timestamp.title}"?`)) return

      try {
        const api = (await import('@/services/api.js')).default
        await api.delete(`/lms/timestamps/${timestamp.id}/`)
        
        toast.success('Timestamp deleted')
        await loadTimestamps()
        emit('updated')
      } catch (err) {
        console.error('Error deleting timestamp:', err)
        toast.error('Failed to delete timestamp')
      }
    }

    const startEdit = (timestamp) => {
      editingTimestamp.value = timestamp.id
      editForm.value = {
        time: formatTime(timestamp.timestamp_seconds),
        title: timestamp.title,
        description: timestamp.description || ''
      }
    }

    const cancelEdit = () => {
      editingTimestamp.value = null
      editForm.value = {
        time: '',
        title: '',
        description: ''
      }
    }

    const saveEdit = async (timestamp) => {
      try {
        const api = (await import('@/services/api.js')).default
        
        const seconds = parseTimeToSeconds(editForm.value.time)
        
        await api.patch(`/lms/timestamps/${timestamp.id}/`, {
          timestamp_seconds: seconds,
          title: editForm.value.title,
          description: editForm.value.description
        })
        
        toast.success('Timestamp updated')
        editingTimestamp.value = null
        await loadTimestamps()
        emit('updated')
      } catch (err) {
        console.error('Error updating timestamp:', err)
        toast.error('Failed to update timestamp: ' + (err.response?.data?.error || err.message))
      }
    }

    onMounted(() => {
      loadTimestamps()
    })

    return {
      timestamps,
      sortedTimestamps,
      newTimestamp,
      isVimeoVideo,
      fetchingThumbnail,
      thumbnailMethod,
      thumbnailFile,
      thumbnailPreviewUrl,
      thumbnailFileInput,
      editingTimestamp,
      editForm,
      addTimestamp,
      deleteTimestamp,
      startEdit,
      cancelEdit,
      saveEdit,
      fetchThumbnail,
      handleThumbnailUpload,
      formatTime
    }
  }
}
</script>

<style scoped>
.timestamp-manager-modal {
  /* Modal overlay styles inherited */
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-lg);
}

.modal-content {
  background: white;
  border-radius: var(--border-radius);
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: var(--spacing-xl);
  border-bottom: 2px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--color-text-secondary);
  padding: var(--spacing-sm);
}

.modal-close:hover {
  color: var(--color-text-primary);
}

.modal-body {
  padding: var(--spacing-xl);
}

.info-text {
  background: #e3f2fd;
  padding: var(--spacing-md);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-xl);
  color: #1565c0;
}

.add-timestamp-section {
  margin-bottom: var(--spacing-xxl);
  padding-bottom: var(--spacing-xxl);
  border-bottom: 2px solid var(--color-border);
}

.add-timestamp-section h3 {
  margin: 0 0 var(--spacing-lg) 0;
  font-size: 1.1rem;
}

.timestamp-form {
  background: #f8f9fa;
  padding: var(--spacing-lg);
  border-radius: var(--border-radius);
}

.timestamps-list-section h3 {
  margin: 0 0 var(--spacing-lg) 0;
  font-size: 1.1rem;
}

.empty-state-small {
  text-align: center;
  padding: var(--spacing-xl);
  background: #f8f9fa;
  border-radius: var(--border-radius);
  color: var(--color-text-secondary);
}

.empty-state-small i {
  font-size: 2rem;
  margin-bottom: var(--spacing-sm);
  opacity: 0.5;
}

.timestamps-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.thumbnail-method-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: var(--spacing-md);
  background: #f3f4f6;
  padding: 0.25rem;
  border-radius: 8px;
}

.method-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
}

.method-tab:hover {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.method-tab.active {
  background: white;
  color: #3b82f6;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.thumbnail-input-section {
  margin-top: var(--spacing-md);
}

.thumbnail-input-group {
  display: flex;
  gap: var(--spacing-sm);
  align-items: flex-start;
}

.thumbnail-input-group .form-input {
  flex: 1;
}

.file-upload-wrapper {
  position: relative;
}

.file-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.file-upload-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: var(--spacing-md);
  background: white;
  border: 2px dashed #d1d5db;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s;
  color: #6b7280;
  font-weight: 500;
}

.file-upload-label:hover {
  border-color: #3b82f6;
  color: #3b82f6;
  background: #eff6ff;
}

.file-upload-label i {
  font-size: 1.25rem;
}

.btn-full {
  width: 100%;
  justify-content: center;
}

.thumbnail-preview {
  margin-top: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
  border-radius: var(--border-radius);
  overflow: hidden;
  border: 2px solid #e5e7eb;
  max-width: 300px;
  position: relative;
}

.thumbnail-preview img {
  width: 100%;
  height: auto;
  display: block;
}

.thumbnail-time-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.875rem;
  font-weight: 700;
}

.timestamp-item {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: #f8f9fa;
  border-radius: var(--border-radius);
  align-items: flex-start;
  transition: all 0.2s;
}

.timestamp-item.editing {
  background: #e0f2fe;
  border: 2px solid #0ea5e9;
}

.timestamp-number {
  width: 30px;
  height: 30px;
  background: var(--color-primary);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
}

.timestamp-thumbnail {
  width: 120px;
  height: 68px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  background: #e5e7eb;
  border: 1px solid #d1d5db;
  position: relative;
}

.timestamp-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-time-overlay {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: monospace;
  font-size: 0.7rem;
  font-weight: 700;
}

.timestamp-info {
  flex: 1;
}

.timestamp-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-xs);
}

.timestamp-time {
  font-family: monospace;
  font-weight: 700;
  color: var(--color-primary);
  background: rgba(var(--color-primary-rgb), 0.1);
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.9rem;
}

.timestamp-header h4 {
  margin: 0;
  font-size: 1rem;
  color: var(--color-text-primary);
}

.timestamp-desc {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.form-group {
  margin-bottom: var(--spacing-md);
}

.form-group label {
  display: block;
  margin-bottom: var(--spacing-xs);
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 0.9rem;
}

.form-input {
  width: 100%;
  padding: var(--spacing-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  font-size: 1rem;
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary);
}

.form-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: var(--spacing-md);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius);
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-small {
  padding: var(--spacing-xs) var(--spacing-md);
  font-size: 0.9rem;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-secondary);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
}

.btn-secondary:hover {
  background: var(--color-primary);
  color: white;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
}

.btn-outline {
  background: white;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
  padding: var(--spacing-sm) var(--spacing-md);
  white-space: nowrap;
}

.btn-outline:hover:not(:disabled) {
  background: var(--color-primary);
  color: white;
}

.btn-outline:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-footer {
  padding: var(--spacing-lg) var(--spacing-xl);
  border-top: 1px solid var(--color-border);
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .thumbnail-input-group {
    flex-direction: column;
  }

  .thumbnail-input-group .btn {
    width: 100%;
  }

  .timestamp-item {
    flex-direction: column;
  }

  .timestamp-thumbnail {
    width: 100%;
    height: auto;
    aspect-ratio: 16/9;
  }

  .timestamp-item .btn {
    width: 100%;
  }
}

/* Edit Form Styles */
.timestamp-edit-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.edit-row {
  display: flex;
  gap: 8px;
}

.edit-input {
  padding: 8px 12px;
  border: 1px solid var(--color-border, #e0e0e0);
  border-radius: 4px;
  font-size: 14px;
}

.edit-input:focus {
  outline: none;
  border-color: var(--color-primary, #00a79d);
}

.time-input {
  width: 100px;
  flex-shrink: 0;
}

.title-input {
  flex: 1;
}

.desc-input {
  width: 100%;
}

.timestamp-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  align-items: flex-start;
}

.btn-small {
  padding: 6px 12px;
  font-size: 13px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-success {
  background: #10b981;
  color: white;
}

.btn-success:hover {
  background: #059669;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover {
  background: #dc2626;
}
</style>

